import { useState } from "react";
import { Screen } from "@/components/screen";
import { Button, Card, Field } from "@/components/ui";
import { useToast } from "@/contexts";
import { searchProfiles, sendFriendRequest } from "@/features/friends/api";
import type { FriendProfile } from "@/types";

const statusConfig = {
  accepted: { label: "Friends", color: "text-aqua", bg: "bg-[rgba(32,191,107,0.1)]" },
  pending: { label: "Pending", color: "text-amber", bg: "bg-[rgba(255,193,7,0.1)]" },
  requested: { label: "Sent", color: "text-amber", bg: "bg-[rgba(255,193,7,0.1)]" },
} as const;

type StatusKey = keyof typeof statusConfig;

function getStatus(key: string | null): { label: string; color: string; bg: string } {
  if (key && key in statusConfig) return statusConfig[key as StatusKey];
  return { label: "Add", color: "text-aqua", bg: "bg-[rgba(255,45,126,0.08)]" };
}

export default function FriendsAdd() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<FriendProfile[]>([]);
  const [searched, setSearched] = useState(false);
  const [searching, setSearching] = useState(false);
  const toast = useToast();

  async function handleSearch() {
    const trimmed = query.trim();
    if (!trimmed) {
      toast.showAlert("Search", "Enter a username, display name, or email to search.", "error");
      return;
    }

    setSearching(true);
    setSearched(true);

    const { data, error } = await searchProfiles(trimmed);
    if (error) {
      toast.showAlert("Error", error.message, "error");
    } else {
      setResults(data ?? []);
    }
    setSearching(false);
  }

  async function handleSendRequest(userId: string, displayName: string) {
    const { error } = await sendFriendRequest(userId);
    if (error) {
      toast.showAlert("Error", error.message, "error");
      return;
    }

    setResults((prev) =>
      prev.map((r) => (r.id === userId ? { ...r, friendshipStatus: "requested" as const } : r)),
    );
    toast.showAlert("Request sent", `Friend request sent to ${displayName}`, "success");
  }

  return (
    <Screen
      eyebrow="Friends"
      title="Add a friend"
      subtitle="Search by username, display name, or email"
    >
      <Card tone="strong">
        <Field
          label="Search"
          value={query}
          onChangeText={setQuery}
          placeholder="Enter username, name, or email..."
        />
        <Button
          label={searching ? "Searching..." : "Search"}
          kind="primary"
          onPress={handleSearch}
        />
      </Card>

      {searching && (
        <span className="text-text-muted text-sm text-center mt-3 block">Searching...</span>
      )}

      {!searching && searched && results.length === 0 && (
        <Card>
          <div className="flex flex-col items-center py-2 gap-1.5">
            <span className="text-2xl">🔍</span>
            <span className="text-text-dim text-sm italic text-center">
              No users matching "{query}"
            </span>
          </div>
        </Card>
      )}

      {results.length > 0 && (
        <div className="bg-card rounded-[20px] p-4 border border-border shadow-[1px_1px_0px_0px_#1c1b2e] flex flex-col gap-1">
          {results.map((user) => {
            const status = getStatus(user.friendshipStatus);
            const isActionable =
              user.friendshipStatus !== "accepted" && user.friendshipStatus !== "requested";

            return (
              <div
                key={user.id}
                className="flex flex-row items-center gap-3 py-2 border-b border-border last:border-0"
              >
                <div className="w-10 h-10 rounded-full bg-[rgba(208,66,255,0.08)] flex items-center justify-center overflow-hidden shrink-0">
                  {user.avatarUrl ? (
                    <img
                      src={user.avatarUrl}
                      alt=""
                      className="w-10 h-10"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <span className="text-purple text-base font-extrabold">
                      {user.displayName.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="flex-1 flex flex-col gap-0.5">
                  <span className="text-text text-sm font-bold">{user.displayName}</span>
                  <span className="text-text-muted text-xs">@{user.username}</span>
                </div>
                {isActionable ? (
                  <button
                    type="button"
                    onClick={() => handleSendRequest(user.id, user.displayName)}
                    className={`px-3 py-2 rounded-lg text-xs font-bold cursor-pointer border-0 font-inherit ${status.bg} ${status.color}`}
                  >
                    {status.label}
                  </button>
                ) : (
                  <span
                    className={`px-2.5 py-1.5 rounded-lg text-xs font-bold ${status.bg} ${status.color}`}
                  >
                    {status.label}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Screen>
  );
}
